import React, { useRef } from 'react';
import { EventData } from '../types/PartnerTypes';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { FLAVOR } from '../../../Config/firebase';
interface MinimalEventCarouselProps {
  events: EventData[];
  partnerId: string;
}

const MinimalEventCarousel: React.FC<MinimalEventCarouselProps> = ({ events, partnerId }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleEventClick = (event: EventData) => {
    if (FLAVOR === "DEVELOPMENT") {
      window.location.href = `https://plundrdev.hosaksham.com/details/${partnerId}/event/${event.id}`;
    } else {
      window.location.href = `https://www.goplundr.com/details/${partnerId}/event/${event.id}`;
    }
  };

  const formatTimestamp = (timestamp: Timestamp | undefined) => {
    if (!timestamp) return '';
    return format(timestamp.toDate(), 'MMM dd, yyyy');
  };

  const formatShortDate = (timestamp: Timestamp | undefined) => {
    if (!timestamp) return '';
    return format(timestamp.toDate(), 'MMM dd');
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth * 0.8; // Scroll 80% of container width
      const newScrollPosition = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      
      container.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="mb-8 p-4 sm:p-6 md:p-10 bg-[#EFEFEF] rounded-xl sm:rounded-2xl md:rounded-3xl border border-gray-200 shadow-sm">
      <div className="relative mb-4 sm:mb-6 md:mb-10">
        <div className="flex items-start justify-between">
          <div className="relative pl-3 sm:pl-5">
            <div className="absolute -left-0 top-1/2 -translate-y-1/2 w-1 h-10 sm:h-12 md:h-16 bg-[#4F46E5] rounded-full"></div>
            <div>
              <span className="text-[#4F46E5] text-xs sm:text-sm font-medium mb-1 block">Join & Learn</span>
              <h3 className="text-xl sm:text-xl font-rubik text-gray-900 mb-1 sm:mb-2 font-semibold tracking-wide">Upcoming Events</h3>
              <p className="text-gray-600 text-xs sm:text-sm">Join our exclusive events designed to inspire and grow</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => scroll('left')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors border border-gray-200 hover:border-[#4F46E5]/30 hover:scale-105 active:scale-95"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
                <path d="m15 18-6-6 6-6"/>
              </svg>
            </button>
            <button 
              onClick={() => scroll('right')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors border border-gray-200 hover:border-[#4F46E5]/30 hover:scale-105 active:scale-95"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-[#4F46E5]/20 via-gray-200 to-transparent"></div>
      </div>
      <div className="overflow-x-auto hide-scrollbar -mx-2 px-2" ref={scrollContainerRef}>
        <div className="flex gap-2 sm:gap-4 md:gap-3 pb-4">
          {events.map((event, index) => (
            <div
              key={index}
              onClick={() => handleEventClick(event)}
              className="flex-none w-[220px] sm:w-[260px] md:w-[260px] bg-white rounded-xl overflow-hidden border border-gray-300 shadow-sm hover:shadow-md hover:shadow-indigo-100/40 transition-all duration-300 cursor-pointer group"
            >
              <div className="relative h-32 sm:h-40 overflow-hidden">
                <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-110">
                  <img
                    src={event.ePromoImage || "https://images.unsplash.com/photo-1535525153412-5a42439a210d?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"}
                    alt={event.eName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
                  event.isOpen ? 'bg-[#10B981]/90' : 'bg-[#BE123C]/90'
                } text-white backdrop-blur-sm z-10 shadow-lg`}>
                  {event.isOpen ? 'Live' : 'Closed'}
                </div>
                <div className="absolute inset-x-0 bottom-0 h-24 sm:h-28 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-[1]"></div>
                <div className="absolute bottom-2 left-3 right-3 z-[2]">
                  <h4 className="font-semibold text-sm sm:text-base text-white mb-0.5 line-clamp-1">{event.eName}</h4>
                  <p className="text-white/90 text-xs line-clamp-2">{event.eSummary}</p>
                </div>
              </div>
              <div className="p-3 sm:p-4">
                <div className="space-y-2 sm:space-y-3">
                  {/* Event Date & Time */}
                  <div className="flex items-center gap-1.5 text-gray-800 text-xs bg-gray-100 p-1.5 sm:p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#4F46E5]">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span className="line-clamp-1">
                      {formatTimestamp(event.eStartTime)}
                    </span>
                  </div>

                  {/* Location */}
                  <div className="flex items-center gap-1.5 text-gray-700 text-xs">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#4F46E5]">
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span className="line-clamp-1">
                      {event?.eGeoLocation?.placeName || event.eVenue}
                    </span>
                  </div>

                  {/* Event Details - Conditionally rendered to save space */}
                  {(event.eRegsStartTime || (Array.isArray(event.participants) && event.participants.length > 0)) && (
                    <div className="space-y-1.5 pt-0.5">
                      {/* Registration Period - Only show if exists */}
                      {event.eRegsStartTime && (
                        <div className="text-[10px] sm:text-xs text-gray-600">
                          <span className="font-medium">Registration:</span>{' '}
                          <span>{formatShortDate(event.eRegsStartTime)} - {formatShortDate(event.eEndTime)}</span>
                        </div>
                      )}

                      {/* Participants Count */}
                      {Array.isArray(event.participants) && event.participants.length > 0 && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-700">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#4F46E5]">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                          <span>{event.participants.length} Participants</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Price and Action */}
                  <div className="flex items-center justify-between pt-1">
                    <div>
                      <span className="text-gray-600 text-[10px] sm:text-xs mr-1">Starts at</span>
                      <span className="text-[#4F46E5] font-bold text-sm sm:text-base">
                        ₹{event.tickets?.[0]?.ticketPrice || 'Free'}
                      </span>
                    </div>
                    <button className="bg-[#4F46E5] text-white px-3 sm:px-4 py-1.5 rounded-full text-xs font-medium hover:shadow-md hover:shadow-indigo-200 hover:bg-indigo-600 active:scale-95 transition-all duration-300">
                      Register
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MinimalEventCarousel; 